/**
 * Retro terminal-themed portfolio client.
 *
 * Features: command history, tab-autocompletion, virtual filesystem traversal
 * (ls / cd / cat), theme switching, simulated curl, and an interactive contact
 * form wizard.
 *
 * DOM elements are lazily cached on first use so that the module can be imported
 * in test environments that do not have a full DOM at load time.
 */

import { fetchApi, API_BASE } from './api.js';
import { getCurrentPath, setCurrentPath, getDirNode } from './fs.js';
import { formatMarkdown } from './markdown.js';
import { contactWizard, startContactWizard, executeContactWizard } from './wizard.js';
import { commandList, fetchBlogSlugsForAutocomplete, handleTabComplete } from './autocomplete.js';
import {
    CONFIG,
    CSS_CLASSES,
    DOM_SELECTORS,
    DOM_EVENTS,
    LOCAL_STORAGE_KEYS,
    COMMANDS,
    BLOG_SUBCOMMANDS,
    API_ENDPOINTS,
    PATHS,
    MESSAGES,
    HTML_TEMPLATES,
    REGEX_PATTERNS,
    FS_ENTRY_TYPES,
    escapeHtml,
    METADATA,
    THEME_LIST,
    THEMES
} from './constants.js';

// Application state variables
export let commandHistory = [];
export let historyIndex = -1;
let loadingProgressVal = 0;
let loadingInterval = null;
let terminalScrollTop = 0;

export function updateLoadingProgress(targetVal, speed, statusText = null) {
    const progressEl = document.getElementById(DOM_SELECTORS.LOADING_PROGRESS);
    const statusEl = document.getElementById(DOM_SELECTORS.LOADING_STATUS);

    if (statusText && statusEl) {
        statusEl.textContent = statusText;
    }

    if (loadingInterval) clearInterval(loadingInterval);

    loadingInterval = setInterval(() => {
        if (loadingProgressVal >= targetVal) {
            clearInterval(loadingInterval);
            return;
        }
        loadingProgressVal += 1;
        if (progressEl) {
            progressEl.style.width = `${loadingProgressVal}%`;
        }
    }, speed);
}

// DOM elements cached
let terminalBody = null;
let terminalOutput = null;
let terminalInput = null;
let terminalText = null;
let cursor = null;
let toggleCrtBtn = null;

export function resetDomCache() {
    terminalBody = null;
    terminalOutput = null;
    terminalInput = null;
    terminalText = null;
    cursor = null;
    toggleCrtBtn = null;
}

export function initLayoutStrings() {
    document.title = METADATA.TITLE;

    const winTitleEl = document.querySelector('.window-title');
    if (winTitleEl) winTitleEl.textContent = `${METADATA.WINDOW_TITLE_EMAIL}: ${METADATA.WINDOW_TITLE_SHELL}`;

    const loadingTitleEl = document.getElementById(DOM_SELECTORS.LOADING_TITLE);
    if (loadingTitleEl) loadingTitleEl.textContent = `${METADATA.LOADING_TITLE_PREFIX}...`;

    const loadingStatusEl = document.getElementById(DOM_SELECTORS.LOADING_STATUS);
    if (loadingStatusEl) loadingStatusEl.textContent = METADATA.LOADING_STATUS_CONNECTING;

    const themeSelectorGroup = document.querySelector(DOM_SELECTORS.THEME_SELECTOR_GROUP);
    if (themeSelectorGroup) {
        themeSelectorGroup.textContent = CONFIG.EMPTY;
        METADATA.THEME_DOTS.forEach(dot => {
            const span = document.createElement('span');
            span.className = `theme-dot ${dot.class}`;
            span.setAttribute(CONFIG.DATA_THEME_ATTR, dot.theme);
            span.setAttribute('title', dot.title);
            themeSelectorGroup.appendChild(span);
        });
    }

    toggleCrtBtn = document.getElementById(DOM_SELECTORS.TOGGLE_CRT);
    if (toggleCrtBtn) {
        toggleCrtBtn.textContent = METADATA.CRT_BTN_TEXT;
        toggleCrtBtn.title = METADATA.CRT_BTN_TITLE;
    }

    const footer = document.querySelector('.terminal-footer');
    if (footer) {
        footer.textContent = CONFIG.EMPTY;
        METADATA.FOOTER_SHORTCUTS.forEach(text => {
            const span = document.createElement('span');
            span.textContent = text;
            footer.appendChild(span);
        });
    }

    resetPrompt();
}

// Initialize website logic
document.addEventListener(DOM_EVENTS.DOM_CONTENT_LOADED, () => {
    // Populate layout text elements dynamically from constants
    initLayoutStrings();

    // Setup SPA history popstate routing architecture
    initRouter();

    // Start progress loading animation
    loadingProgressVal = 0;
    updateLoadingProgress(45, 10, METADATA.LOADING_STATUS_CONNECTING);
    setTimeout(() => {
        if (loadingProgressVal < 80) {
            updateLoadingProgress(80, 20, METADATA.LOADING_STATUS_DIAGNOSTICS);
        }
    }, 450);

    // Print startup banner and await network response to finish loader
    const bannerPromise = printBanner();

    const handleLoaderCompletion = (data) => {
        if (data && data.appVersion) {
            const loadingTitleEl = document.getElementById(DOM_SELECTORS.LOADING_TITLE);
            if (loadingTitleEl) {
                loadingTitleEl.textContent = `${METADATA.LOADING_TITLE_PREFIX} v${data.appVersion}...`;
            }
        }
        updateLoadingProgress(100, 2, METADATA.LOADING_STATUS_COMPLETED);
        setTimeout(() => {
            const loader = document.getElementById(DOM_SELECTORS.LOADING_SCREEN);
            if (loader) {
                loader.classList.add('hidden');
            }
            focusInput();
        }, 300);
    };

    if (bannerPromise && typeof bannerPromise.then === 'function') {
        bannerPromise
            .then(data => handleLoaderCompletion(data))
            .catch(() => handleLoaderCompletion(null));
    } else {
        handleLoaderCompletion(null);
    }

    document.body.addEventListener(DOM_EVENTS.CLICK, (e) => {
        if (!e.target.closest(DOM_SELECTORS.THEME_SELECTOR_GROUP) && !e.target.closest(DOM_SELECTORS.CURSOR)) {
            focusInput();
        }
    });

    // Theme selector listeners (using event delegation)
    const themeSelectorGroup = document.querySelector(DOM_SELECTORS.THEME_SELECTOR_GROUP);
    if (themeSelectorGroup) {
        themeSelectorGroup.addEventListener(DOM_EVENTS.CLICK, (e) => {
            const dot = e.target.closest(DOM_SELECTORS.THEME_DOT);
            if (dot) {
                const selectedTheme = dot.getAttribute(CONFIG.DATA_THEME_ATTR);
                changeTheme(selectedTheme);
            }
        });
    }

    // CRT overlay toggle listener
    if (toggleCrtBtn) {
        toggleCrtBtn.addEventListener(DOM_EVENTS.CLICK, () => {
            document.body.classList.toggle(CSS_CLASSES.CRT_EFFECT);
            const isActive = document.body.classList.contains(CSS_CLASSES.CRT_EFFECT);
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(LOCAL_STORAGE_KEYS.CRT_EFFECT, isActive ? CONFIG.TRUE : CONFIG.FALSE);
            }
        });
    }

    // Keyboard and typing event listeners
    terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (terminalInput) {
        terminalInput.addEventListener(DOM_EVENTS.INPUT, updateInputDisplay);
        terminalInput.addEventListener(DOM_EVENTS.KEYDOWN, handleKeyInput);
    }

    // Load saved preferences
    loadSavedSettings();

    // Cache blog slugs for command autocomplete
    fetchBlogSlugsForAutocomplete();

    const readerBackBtn = document.getElementById('reader-back-btn');
    if (readerBackBtn) {
        readerBackBtn.addEventListener(DOM_EVENTS.CLICK, () => {
            window.location.hash = '';
        });
    }
});

// Load preferences from local storage
export function loadSavedSettings() {
    let savedTheme = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEYS.THEME) : null;
    if (!savedTheme || !THEME_LIST.includes(savedTheme)) {
        savedTheme = THEMES.DRACULA;
    }
    changeTheme(savedTheme);

    const crtEnabled = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEYS.CRT_EFFECT) : null;
    if (crtEnabled === CONFIG.FALSE) {
        document.body.classList.remove(CSS_CLASSES.CRT_EFFECT);
    } else {
        document.body.classList.add(CSS_CLASSES.CRT_EFFECT);
    }
}

// Change terminal CSS theme skin
export function changeTheme(themeName) {
    document.body.className = document.body.className.replace(/\btheme-\S+/g, CONFIG.EMPTY);
    document.body.classList.add(`theme-${themeName}`);
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LOCAL_STORAGE_KEYS.THEME, themeName);
    }
}

// Focus typing area and blink cursor
export function focusInput() {
    if (!terminalInput) terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (!cursor) cursor = document.querySelector(DOM_SELECTORS.CURSOR);
    if (terminalInput) {
        terminalInput.focus();
    }
    if (cursor) {
        cursor.classList.remove(CSS_CLASSES.BLINKING_CURSOR);
    }
    setTimeout(() => {
        if (terminalInput && document.activeElement === terminalInput) {
            if (cursor) cursor.classList.add(CSS_CLASSES.BLINKING_CURSOR);
        }
    }, 1500);
}

// Update visually matching overlay text mirroring standard hidden input values
export function updateInputDisplay() {
    if (!terminalText) terminalText = document.getElementById(DOM_SELECTORS.TERMINAL_TEXT);
    if (!terminalInput) terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (terminalText && terminalInput) {
        terminalText.textContent = terminalInput.value;
    }
}

// Handle key binds
export function handleKeyInput(e) {
    if (!terminalInput) terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (e.key === CONFIG.KEY_ENTER) {
        const inputVal = terminalInput.value.trim();
        terminalInput.value = CONFIG.EMPTY;
        updateInputDisplay();

        if (inputVal || contactWizard.active) {
            processCommand(inputVal);
        }
    } else if (e.key === CONFIG.KEY_UP) {
        e.preventDefault();
        cycleHistory(1);
    } else if (e.key === CONFIG.KEY_DOWN) {
        e.preventDefault();
        cycleHistory(-1);
    } else if (e.key === CONFIG.KEY_TAB) {
        e.preventDefault();
        handleTabComplete();
    }
}

// History traversal logic
export function cycleHistory(direction) {
    if (!terminalInput) terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (commandHistory.length === 0 || contactWizard.active) return;

    if (historyIndex === -1) {
        historyIndex = commandHistory.length;
    }

    historyIndex -= direction;

    if (historyIndex < 0) {
        historyIndex = 0;
    } else if (historyIndex >= commandHistory.length) {
        historyIndex = -1;
        if (terminalInput) terminalInput.value = CONFIG.EMPTY;
    } else {
        if (terminalInput) terminalInput.value = commandHistory[historyIndex];
    }
    updateInputDisplay();
}

// Print standard text line to screen
export function printLine(text, className = CONFIG.EMPTY) {
    if (!terminalOutput) terminalOutput = document.getElementById(DOM_SELECTORS.TERMINAL_OUTPUT);
    if (!terminalBody) terminalBody = document.getElementById(DOM_SELECTORS.TERMINAL_BODY);

    const el = document.createElement('div');
    el.className = className;

    if (text) {
        if (text instanceof HTMLElement) {
            el.appendChild(text);
        } else {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            while (doc.body.firstChild) {
                el.appendChild(doc.body.firstChild);
            }
        }
    }

    if (terminalOutput) terminalOutput.appendChild(el);
    if (terminalBody) terminalBody.scrollTop = terminalBody.scrollHeight;
}

// Print startup system logo and commands listing
export function printBanner() {
    const isMobile = window.innerWidth <= 768;
    const logo = isMobile
        ? `${MESSAGES.ASCII_LOGO_PART1}\n${MESSAGES.ASCII_LOGO_PART2}`
        : MESSAGES.ASCII_LOGO;
    printLine(logo, `${CSS_CLASSES.ACCENT} ${CSS_CLASSES.ASCII_BANNER}`);
    printLine(MESSAGES.WELCOME_TITLE, CSS_CLASSES.TITLE);
    printLine(MESSAGES.LOADING_DIAGNOSTICS, `${CSS_CLASSES.COMMENT} ${CSS_CLASSES.BANNER_SYSTEMS}`);
    printLine(MESSAGES.WELCOME_HELP_HINT);

    const url = `${API_BASE}${API_ENDPOINTS.SYSINFO}`;
    const fetchPromise = fetch(url);
    if (fetchPromise && typeof fetchPromise.then === 'function') {
        return fetchPromise
            .then(res => {
                if (res.ok) return res.json();
                throw new Error();
            })
            .then(data => {
                const el = document.querySelector(DOM_SELECTORS.BANNER_SYSTEMS);
                if (el) {
                    el.textContent = MESSAGES.SYSTEMS_INFO(data.springBootVersion, data.javaVersion);
                }
                return data;
            })
            .catch(() => {
                const el = document.querySelector(DOM_SELECTORS.BANNER_SYSTEMS);
                if (el) {
                    el.textContent = MESSAGES.DEFAULT_SYSTEMS_INFO;
                }
            });
    } else {
        const el = document.querySelector(DOM_SELECTORS.BANNER_SYSTEMS);
        if (el) {
            el.textContent = MESSAGES.DEFAULT_SYSTEMS_INFO;
        }
    }
    return null;
}

// Main execution broker routing logic
export function processCommand(rawInput) {
    if (contactWizard.active) {
        executeContactWizard(rawInput);
        return;
    }

    commandHistory.push(rawInput);
    historyIndex = -1;

    const current = getCurrentPath();
    const promptPath = current === PATHS.ROOT ? PATHS.HOME : `~${current}`;
    printLine(HTML_TEMPLATES.PROMPT_ECHO(promptPath, rawInput));

    const parts = rawInput.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch(cmd) {
        case COMMANDS.HELP:
            cmdHelp();
            break;
        case COMMANDS.ABOUT:
            cmdAbout();
            break;
        case COMMANDS.SKILLS:
            cmdSkills();
            break;
        case COMMANDS.PROJECTS:
            cmdProjects();
            break;
        case COMMANDS.EXPERIENCE:
            cmdExperience();
            break;
        case COMMANDS.SYSINFO:
            cmdSysInfo();
            break;
        case COMMANDS.CLEAR:
            if (!terminalOutput) terminalOutput = document.getElementById(DOM_SELECTORS.TERMINAL_OUTPUT);
            if (terminalOutput) terminalOutput.textContent = CONFIG.EMPTY;
            break;
        case COMMANDS.THEME:
            cmdTheme(args);
            break;
        case COMMANDS.LS:
            cmdLs();
            break;
        case COMMANDS.CD:
            cmdCd(args);
            break;
        case COMMANDS.CAT:
            cmdCat(args);
            break;
        case COMMANDS.CURL:
            cmdCurl(args);
            break;
        case COMMANDS.CONTACT:
            startContactWizard();
            break;
        case COMMANDS.BLOG:
            cmdBlog(args);
            break;
        case COMMANDS.WHOAMI:
            printLine('guest');
            break;
        default:
            const suggestions = commandList.filter(c => c.startsWith(cmd.substring(0, 2)));
            const container = document.createElement('div');
            container.appendChild(document.createTextNode(`bash: command not found: ${cmd}.`));
            if (suggestions && suggestions.length > 0) {
                container.appendChild(document.createTextNode(' Did you mean: '));
                const suggestionSpan = document.createElement('span');
                suggestionSpan.className = CSS_CLASSES.ACCENT;
                suggestionSpan.textContent = suggestions.join(', ');
                container.appendChild(suggestionSpan);
                container.appendChild(document.createTextNode('?'));
            } else {
                container.appendChild(document.createTextNode(` Type 'help' to see list of valid commands.`));
            }
            printLine(container, CSS_CLASSES.ERROR);
    }
}

/* =========================================================
   COMMAND IMPLEMENTATIONS
   ========================================================= */

export function cmdHelp() {
    printLine(MESSAGES.HELP_TEXT);
}

export function cmdAbout() {
    fetchApi(API_ENDPOINTS.PROFILE, (data) => {
        const aboutContainer = document.createElement('div');
        aboutContainer.style.whiteSpace = 'pre-wrap';

        aboutContainer.appendChild(document.createTextNode('\n'));
        const nameSpan = document.createElement('span');
        nameSpan.className = 'text-title';
        nameSpan.textContent = data.name;
        aboutContainer.appendChild(nameSpan);
        aboutContainer.appendChild(document.createTextNode('\n'));

        const titleSpan = document.createElement('span');
        titleSpan.className = 'text-accent';
        titleSpan.textContent = data.title;
        aboutContainer.appendChild(titleSpan);

        aboutContainer.appendChild(document.createTextNode(`\nLocation: ${data.location}\nEmail:    ${data.email}\n\n${data.bio}\n`));
        printLine(aboutContainer);

        printLine('Social Links:', CSS_CLASSES.COMMENT);
        Object.entries(data.socialLinks).forEach(([key, val]) => {
            const linkContainer = document.createElement('div');
            linkContainer.appendChild(document.createTextNode(`  - ${key}: `));
            const link = document.createElement('a');
            link.href = val;
            link.target = '_blank';
            link.className = 'text-accent';
            link.textContent = val;
            linkContainer.appendChild(link);
            printLine(linkContainer);
        });
        printLine(CONFIG.EMPTY);
    });
}

export function cmdSkills() {
    fetchApi(API_ENDPOINTS.SKILLS, (data) => {
        printLine('\nTechnical Skills Tree:', CSS_CLASSES.TITLE);
        let gridHtml = CONFIG.EMPTY;
        data.forEach(cat => {
            gridHtml += HTML_TEMPLATES.SKILLS_GRID_CATEGORY(cat.category, cat.items);
        });
        printLine(HTML_TEMPLATES.SKILLS_GRID_CONTAINER(gridHtml));
    });
}

export function cmdProjects() {
    fetchApi(API_ENDPOINTS.PROJECTS, (data) => {
        printLine('\nFeatured Projects:', CSS_CLASSES.TITLE);
        data.forEach(proj => {
            const tags = proj.techStack.map(t => HTML_TEMPLATES.TECH_TAG(t)).join(CONFIG.SPACE);
            printLine(HTML_TEMPLATES.PROJECT_CARD(proj.name, proj.role, proj.githubUrl, proj.description, tags));
        });
    });
}

export function cmdExperience() {
    fetchApi(API_ENDPOINTS.EXPERIENCE, (data) => {
        printLine('\nWork History & Experience:', CSS_CLASSES.TITLE);
        data.forEach(exp => {
            const bullets = exp.bulletPoints.map(b => `<li>${escapeHtml(b)}</li>`).join(CONFIG.EMPTY);
            printLine(HTML_TEMPLATES.EXP_CARD(exp.role, exp.company, exp.duration, bullets));
        });
    });
}

export function cmdSysInfo() {
    fetchApi(API_ENDPOINTS.SYSINFO, (data) => {
        printLine('\nServer Runtime Diagnostics (JVM 25 / Spring Boot 4):', CSS_CLASSES.TITLE);
        printLine(HTML_TEMPLATES.SYS_GRID(
            data.javaVersion,
            data.springBootVersion,
            data.uptimeSeconds,
            data.totalMemoryMb,
            data.freeMemoryMb,
            data.availableProcessors,
            data.osName,
            data.osArch
        ));
    });
}

export function cmdTheme(args) {
    if (args.length === 0) {
        printLine(MESSAGES.CMD_THEME_USAGE);

        const container = document.createElement('div');
        container.appendChild(document.createTextNode('Available themes: '));
        const accentSpan = document.createElement('span');
        accentSpan.className = CSS_CLASSES.ACCENT;
        accentSpan.textContent = THEME_LIST.join(', ');
        container.appendChild(accentSpan);

        printLine(container);
        return;
    }

    const newTheme = args[0].toLowerCase();
    if (THEME_LIST.includes(newTheme)) {
        changeTheme(newTheme);
        printLine(MESSAGES.CMD_THEME_SWITCHED(newTheme), CSS_CLASSES.SUCCESS);
    } else {
        printLine(MESSAGES.CMD_THEME_INVALID(newTheme, THEME_LIST), CSS_CLASSES.ERROR);
    }
}

export function cmdLs() {
    const node = getDirNode(getCurrentPath());
    if (node && node.type === FS_ENTRY_TYPES.DIR) {
        const files = Object.entries(node.children).map(([name, val]) => {
            if (val.type === FS_ENTRY_TYPES.DIR) {
                return HTML_TEMPLATES.LS_DIR_ENTRY(name);
            } else if (name.endsWith('.sh')) {
                return HTML_TEMPLATES.LS_EXEC_ENTRY(name);
            }
            return name;
        });

        if (files.length === 0) {
            printLine(MESSAGES.CMD_LS_EMPTY);
        } else {
            printLine(files.join('    '));
        }
    }
}

export function cmdCd(args) {
    if (args.length === 0 || args[0] === PATHS.HOME) {
        setCurrentPath(PATHS.ROOT);
        const el = document.querySelector(DOM_SELECTORS.INPUT_LINE_PATH_PREFIX);
        if (el) el.textContent = PATHS.HOME;
        return;
    }

    const targetDirName = args[0];
    if (targetDirName === PATHS.PARENT) {
        const current = getCurrentPath();
        if (current === PATHS.ROOT) return;
        const parts = current.split('/').filter(p => p);
        parts.pop();
        const newPath = '/' + parts.join('/');
        setCurrentPath(newPath);
        const el = document.querySelector(DOM_SELECTORS.INPUT_LINE_PATH_PREFIX);
        if (el) el.textContent = newPath === PATHS.ROOT ? PATHS.HOME : `~${newPath}`;
        return;
    }

    const node = getDirNode(targetDirName);
    if (node && node.type === FS_ENTRY_TYPES.DIR) {
        let newPath;
        if (targetDirName.startsWith(PATHS.ROOT)) {
            newPath = targetDirName;
        } else {
            const current = getCurrentPath();
            newPath = current === PATHS.ROOT ? `/${targetDirName}` : `${current}/${targetDirName}`;
        }
        setCurrentPath(newPath);
        const el = document.querySelector(DOM_SELECTORS.INPUT_LINE_PATH_PREFIX);
        if (el) el.textContent = `~${newPath}`;
    } else {
        printLine(MESSAGES.CMD_CD_NOT_FOUND(targetDirName), CSS_CLASSES.ERROR);
    }
}

export function cmdCat(args) {
    if (args.length === 0) {
        printLine(MESSAGES.CMD_CAT_USAGE);
        return;
    }

    const fileName = args[0];
    const node = getDirNode(fileName);
    if (node && node.type === FS_ENTRY_TYPES.FILE) {
        printLine(node.content);
    } else if (node && node.type === FS_ENTRY_TYPES.DIR) {
        printLine(MESSAGES.CMD_CAT_DIR(fileName), CSS_CLASSES.ERROR);
    } else {
        printLine(MESSAGES.CMD_CAT_NOT_FOUND(fileName), CSS_CLASSES.ERROR);
    }
}

export function cmdCurl(args) {
    if (args.length === 0) {
        printLine(MESSAGES.CMD_CURL_USAGE);
        return;
    }

    let endpoint = CONFIG.EMPTY;
    for (let arg of args) {
        if (arg.startsWith('/api/')) {
            endpoint = arg;
            break;
        }
    }

    if (!endpoint) {
        printLine(MESSAGES.CMD_CURL_INVALID, CSS_CLASSES.ERROR);
        return;
    }

    fetchApi(endpoint, (data) => {
        const colorized = colorizeJson(data);
        printLine(colorized);
    });
}

export function colorizeJson(json) {
    if (typeof json !== 'string') {
        json = JSON.stringify(json, null, 2);
    }
    return json.replace(REGEX_PATTERNS.JSON_COLORIZER, function (match) {
        let cls = CSS_CLASSES.JSON_NUMBER;
        if (/^"/.test(match)) {
            if (/:$/.test(match)) {
                cls = CSS_CLASSES.JSON_KEY;
            } else {
                cls = CSS_CLASSES.JSON_STRING;
            }
        } else if (/true|false/.test(match)) {
            cls = CSS_CLASSES.JSON_BOOLEAN;
        } else if (/null/.test(match)) {
            cls = CSS_CLASSES.JSON_NULL;
        }
        return '<span class="' + cls + '">' + escapeHtml(match) + '</span>';
    });
}

export function setPromptTo(promptText) {
    const el = document.querySelector(DOM_SELECTORS.INPUT_LINE_PROMPT_PREFIX);
    if (el) {
        el.textContent = CONFIG.EMPTY;
        const span = document.createElement('span');
        span.className = CSS_CLASSES.ACCENT;
        span.textContent = promptText;
        el.appendChild(span);
    }
}

export function resetPrompt() {
    const el = document.querySelector(DOM_SELECTORS.INPUT_LINE_PROMPT_PREFIX);
    if (el) {
        el.textContent = CONFIG.EMPTY;
        const current = getCurrentPath();
        const promptPath = current === PATHS.ROOT ? PATHS.HOME : `~${current}`;

        const userSpan = document.createElement('span');
        userSpan.className = CSS_CLASSES.PROMPT_USER;
        userSpan.textContent = 'guest@adiwal.com';

        const pathSpan = document.createElement('span');
        pathSpan.className = CSS_CLASSES.PATH_PREFIX;
        pathSpan.textContent = promptPath;

        el.appendChild(userSpan);
        el.appendChild(document.createTextNode(':'));
        el.appendChild(pathSpan);
        el.appendChild(document.createTextNode('$'));
    }
}

export function cmdBlog(args) {
    if (args.length === 0 || args[0] === BLOG_SUBCOMMANDS.HELP) {
        printLine(MESSAGES.CMD_BLOG_HELP);
        return;
    }

    const subCmd = args[0].toLowerCase();
    if (subCmd === BLOG_SUBCOMMANDS.LIST) {
        const page = (args.length > 1 && !isNaN(args[1])) ? Math.max(1, parseInt(args[1])) : 1;
        fetchApi(`${API_ENDPOINTS.BLOGS}?page=${page}&size=10`, (data, response) => {
            if (!data || data.length === 0) {
                printLine(MESSAGES.CMD_BLOG_EMPTY, CSS_CLASSES.WARNING);
                return;
            }

            let totalPages = 1;
            if (response && response.headers) {
                const totalPagesHeader = response.headers.get('X-Total-Pages');
                if (totalPagesHeader) {
                    totalPages = parseInt(totalPagesHeader) || 1;
                }
            }

            printLine(`\nAvailable Articles (Page ${page} of ${totalPages}):`, CSS_CLASSES.TITLE);
            data.forEach(post => {
                const date = new Date(post.publishedAt).toLocaleDateString();
                const source = post.origin === 'MEDIUM' ? 'Medium' : 'Local';
                const uniqueUrl = `${post.id}-${post.slug}`;

                const container = document.createElement('div');
                container.appendChild(document.createTextNode('  - '));
                const urlSpan = document.createElement('span');
                urlSpan.className = 'text-accent';
                urlSpan.style.cursor = 'pointer';
                urlSpan.style.textDecoration = 'underline';
                urlSpan.textContent = uniqueUrl;

                let isFetchingBlog = false;
                urlSpan.addEventListener('click', async () => {
                    if (isFetchingBlog) return;

                    const slug = uniqueUrl;
                    let blogUrl = API_ENDPOINTS.BLOG_DETAIL(slug);

                    try {
                        isFetchingBlog = true;
                        await fetchApi(blogUrl, (postData) => {
                            if (!postData) {
                                printLine("Error: Could not load blog content.", CSS_CLASSES.ERROR);
                                return;
                            }
                            // openBlogReader handles the pushState internally
                            openBlogReader(postData);
                        });
                    } catch (error) {
                        printLine("Error: A network error occurred.", CSS_CLASSES.ERROR);
                        console.error(error);
                    } finally {
                        isFetchingBlog = false;
                    }
                });

                container.appendChild(urlSpan);
                container.appendChild(document.createTextNode(` : "${post.title}" (${source}) - ${date}`));
                printLine(container);
            });

            if (totalPages > 1) {
                const navContainer = document.createElement('div');
                navContainer.style.marginTop = '12px';
                navContainer.style.marginBottom = '6px';

                if (page > 1) {
                    const prevSpan = document.createElement('span');
                    prevSpan.className = 'text-accent';
                    prevSpan.style.cursor = 'pointer';
                    prevSpan.style.textDecoration = 'underline';
                    prevSpan.style.marginRight = '15px';
                    prevSpan.textContent = '< Prev Page';
                    prevSpan.addEventListener('click', () => {
                        processCommand(`blog list ${page - 1}`);
                    });
                    navContainer.appendChild(prevSpan);
                }

                const pageInfo = document.createElement('span');
                pageInfo.className = 'text-comment';
                pageInfo.textContent = `Page ${page} of ${totalPages}`;
                navContainer.appendChild(pageInfo);

                if (page < totalPages) {
                    const nextSpan = document.createElement('span');
                    nextSpan.className = 'text-accent';
                    nextSpan.style.cursor = 'pointer';
                    nextSpan.style.textDecoration = 'underline';
                    nextSpan.style.marginLeft = '15px';
                    nextSpan.textContent = 'Next Page >';
                    nextSpan.addEventListener('click', () => {
                        processCommand(`blog list ${page + 1}`);
                    });
                    navContainer.appendChild(nextSpan);
                }
                printLine(navContainer);
            }
            printLine(CONFIG.EMPTY);
        });
    } else if (subCmd === BLOG_SUBCOMMANDS.VIEW) {
        if (args.length < 2) {
            printLine(MESSAGES.CMD_BLOG_VIEW_USAGE, CSS_CLASSES.ERROR);
            return;
        }
        const slugOrId = args[1];
        fetchApi(API_ENDPOINTS.BLOG_DETAIL(slugOrId), (post) => {
            if (!post) {
                printLine(MESSAGES.CMD_BLOG_VIEW_NOT_FOUND(slugOrId), CSS_CLASSES.ERROR);
                return;
            }
            // Open the full-screen reader (also updates URL via history.pushState)
            openBlogReader(post);
        });
    } else {
        printLine(MESSAGES.CMD_BLOG_INVALID_SUB(subCmd), CSS_CLASSES.ERROR);
    }
}

export function openBlogReader(post) {
    const terminalWindow = document.querySelector('.terminal-window');
    const blogReaderView = document.getElementById('blog-reader-view');
    const readerContent = document.getElementById('reader-content');
    const terminalBodyEl = document.getElementById(DOM_SELECTORS.TERMINAL_BODY);

    // Save scroll position only when coming from the terminal (not a direct URL hit)
    if (terminalBodyEl && terminalWindow && terminalWindow.style.display !== 'none') {
        terminalScrollTop = terminalBodyEl.scrollTop;
    }

    // Update URL hash so the post is shareable and browser back works
    const uniqueSlug = `${post.id}-${post.slug}`;
    if (window.location.hash !== `#/blog/${uniqueSlug}`) {
        window.location.hash = `/blog/${uniqueSlug}`;
    }

    if (terminalWindow) terminalWindow.style.display = 'none';
    if (blogReaderView) {
        blogReaderView.classList.remove('hidden');
        blogReaderView.scrollTop = 0;
    }

    // Update page title for the open post
    document.title = `${post.title} | Vikas Adiwal`;

    if (readerContent) {
        readerContent.replaceChildren();

        const headerContainer = document.createElement('div');
        headerContainer.style.whiteSpace = 'pre-wrap';
        headerContainer.style.marginBottom = '20px';

        const date = new Date(post.publishedAt).toLocaleDateString();

        const titleEl = document.createElement('h1');
        titleEl.className = 'text-title';
        titleEl.style.fontSize = '2.2rem';
        titleEl.style.margin = '10px 0';
        titleEl.textContent = post.title;
        headerContainer.appendChild(titleEl);

        const metaEl = document.createElement('div');
        metaEl.className = 'text-comment';
        metaEl.textContent = `Published: ${date} | Origin: ${post.origin}`;
        headerContainer.appendChild(metaEl);

        if (post.linkUrl) {
            const linkDiv = document.createElement('div');
            linkDiv.appendChild(document.createTextNode('Original link: '));
            const link = document.createElement('a');
            link.href = post.linkUrl;
            link.target = '_blank';
            link.className = 'text-accent';
            link.textContent = post.linkUrl;
            linkDiv.appendChild(link);
            headerContainer.appendChild(linkDiv);
        }

        readerContent.appendChild(headerContainer);

        if (post.imageUrl) {
            const imgDiv = document.createElement('div');
            imgDiv.className = 'terminal-media-img';
            imgDiv.style.marginBottom = '20px';
            const img = document.createElement('img');
            img.src = post.imageUrl;
            img.alt = post.title;
            img.onerror = () => {
                imgDiv.remove();
            };
            imgDiv.appendChild(img);
            readerContent.appendChild(imgDiv);
        }

        const bodyDiv = document.createElement('div');
        const formattedBody = formatMarkdown(post.content);
        if (formattedBody instanceof HTMLElement) {
            bodyDiv.appendChild(formattedBody);
        } else {
            const parser = new DOMParser();
            const doc = parser.parseFromString(formattedBody, 'text/html');
            while (doc.body.firstChild) {
                bodyDiv.appendChild(doc.body.firstChild);
            }
        }
        readerContent.appendChild(bodyDiv);
    }
}

export function initRouter() {

    // Deep-link: if the page loaded with #/blog/{slug}, show reader immediately
    const checkHashRoute = () => {
        const hashMatch = window.location.hash.match(/^#\/blog\/(.+)$/);
        if (hashMatch) {
            const slug = hashMatch[1];
            fetchBlogPost(slug, /* directEntry= */ true);
        } else {
            // If hash is cleared or empty, return to terminal
            const terminalWindow = document.querySelector('.terminal-window');
            const blogReaderView = document.getElementById('blog-reader-view');
            document.title = METADATA.TITLE;
            if (blogReaderView) blogReaderView.classList.add('hidden');
            if (terminalWindow) terminalWindow.style.display = '';
            const terminalBodyEl = document.getElementById(DOM_SELECTORS.TERMINAL_BODY);
            if (terminalBodyEl) terminalBodyEl.scrollTop = terminalScrollTop;
            focusInput();
        }
    };

    window.addEventListener('hashchange', checkHashRoute);

    // Initial check on load
    checkHashRoute();
}

/**
 * Fetches a blog post by slug/id and opens the reader.
 * @param {string}  slug        - The id-slug identifier (e.g. "1-java-25-concurrency")
 * @param {boolean} directEntry - True when arriving via direct URL (no terminal to return to)
 */
export function fetchBlogPost(slug, directEntry = false) {
    const url = `${API_BASE}${API_ENDPOINTS.BLOG_DETAIL(slug)}`;

    // Only print to terminal if we arrived via the terminal UI, not a direct URL
    if (!directEntry) {
        printLine(`Fetching blog post...`, CSS_CLASSES.COMMENT);
    }

    fetch(url)
        .then(res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        })
        .then(post => {
            openBlogReader(post);
        })
        .catch(err => {
            if (!directEntry) {
                printLine(`Error: Could not load blog post "${slug}".`, CSS_CLASSES.ERROR);
            } else {
                // Direct URL hit but post not found — gracefully fall back to terminal
                const terminalWindow = document.querySelector('.terminal-window');
                const blogReaderView = document.getElementById('blog-reader-view');
                if (blogReaderView) blogReaderView.classList.add('hidden');
                if (terminalWindow) terminalWindow.style.display = '';
                window.location.hash = '';
                printLine(`Blog post "${slug}" not found.`, CSS_CLASSES.ERROR);
            }
            console.error('fetchBlogPost error:', err);
        });
}